// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

// Import global utils
import { isEmpty } from '@automator/shared/utils/is-empty';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __ } from '@wordpress/i18n';

// Import style for web component
import styles from './web-component-style.scss?inline';

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-template-header' )
export class AutomatorTemplateHeader extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	/**
	 * The title of the recipe.
	 *
	 * @type {String}
	 * @property {String} title - The title displayed at the top of the header.
	 * Attribute name in HTML: `recipe-title`.
	 */
	@property( { type: String, attribute: 'recipe-title' } ) accessor title;

	/**
	 * The description of the recipe.
	 *
	 * @type {String}
	 * @property {String} description - A brief description displayed below the title.
	 * Attribute name in HTML: `recipe-description`.
	 */
	@property( { type: String, attribute: 'recipe-description' } ) accessor description;

	/**
	 * Indicates whether the recipe requires the Automator Pro
	 *
	 * @type {Boolean}
	 * @property {Boolean} requiresPro - Specifies if the recipe requires a pro license.
	 * Attribute name in HTML: `requires-pro`.
	 * Default: `false`.
	 */
	@property( { type: Boolean, attribute: 'requires-pro' } )
	accessor requiresPro = false;

	/**
	 * Indicates whether the recipe requires the Automator Elite
	 *
	 * @type {Boolean}
	 * @property {Boolean} requiresElite - Specifies if the recipe requires an elite license.
	 * Attribute name in HTML: `requires-elite`.
	 * Default: `false`.
	 */
	@property( { type: Boolean, attribute: 'requires-elite' } )
	accessor requiresElite = false;

	/**
	 * An array containing summary details of the recipe.
	 * Each object in the array represents a key-value pair summarizing the recipe data.
	 *
	 * @type {Array<{key: TemplateResult, value: String}>}
	 * @property {Array<{key: TemplateResult, value: String}>} summary - A summary array including details such as the recipe type
	 * Attribute name in HTML: none (non-reflected property).
	 */
	@property( { type: Array, attribute: false } ) accessor summary = [];

	// eslint-disable-next-line require-jsdoc
	render() {
		return html`
			<div class="template-header">
				${ this._templateTitle }
				${ this._templateDescription }
				${ this._templateSummary }
			</div>
		`;
	}

	/**
	 * Returns the template that contains the title
	 *
	 * @return {TemplateResult} The title
	 */
	get _templateTitle() {
		return html`
			<h1 class="template-header-title">
				${ this.title } 
				${ this._templatePremiumBadge }
			</h1>
		`;
	}

	/**
	 * Gets the pro badge for the template.
	 *
	 * @return {TemplateResult|undefined} The pro badge element.
	 */
	get _templatePremiumBadge() {
		// Check if it requires Pro
		if ( this.requiresPro ) {
			return html`
				<uo-chip 
					color="success"
					filled
				>
					${ escapeHTML( __( 'Pro', 'uncanny-automator' ) ) }
				</uo-chip>
			`;
		}

		// Check if it requires Elite
		if ( this.requiresElite ) {
			return html`
				<uo-chip 
					color="success"
					filled
				>
					${ escapeHTML( __( 'Pro', 'uncanny-automator' ) ) }
				</uo-chip>
			`;
		}
	}

	/**
	 * Returns the template that contains the description
	 *
	 * @return {TemplateResult|undefined} The description
	 */
	get _templateDescription() {
		if ( isEmpty( this.description ) ) {
			return;
		}

		return html`
			<div class="template-header-description">${ this.description }</div>
		`;
	}

	/**
	 * Returns the table that contains a summary of the recipe
	 *
	 * @return {TemplateResult} The summary of the recipe run
	 */
	get _templateSummary() {
		return html`
			<table class="template-header-summary">
				<tbody>
					${
						repeat(
							this.summary,
							( row, index ) => index,
							row => html`
								<tr>
									<td class="template-header-summary__key">
										${ row.key }
									</td>
									<td class="template-header-summary__value">
										${ row.value }
									</td>
								</tr>
							`
						)
					}
				</tbody>
			</table>
		`;
	}
}